/**
 * Authentication utilities for handling JWT tokens and auth state
 */
import { debugLog } from './debug';

const TOKEN_STORAGE_KEY = 'auth_token';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5444';

export interface AuthUser {
  email: string;
  name: string;
  roles?: string[];
  [key: string]: any;
}

/**
 * Store JWT token in localStorage
 */
export function storeToken(token: string): void {
  debugLog(1, '[Auth] Storing JWT token');
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  debugLog(1, '[Auth] JWT token stored successfully');
}

/**
 * Get JWT token from localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * Decode JWT token and extract user email
 * Note: This is client-side decoding only, not validation
 */
export function getUserEmailFromToken(): string | null {
  const token = getToken();
  if (!token) {
    return null;
  }

  try {
    // JWT token has 3 parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (base64url)
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload?.user?.email || null;
  } catch (error) {
    console.error('[Auth] Failed to decode token:', error);
    return null;
  }
}

/**
 * Remove JWT token from localStorage
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Check if user is authenticated by validating token with backend
 */
export async function checkAuthStatus(): Promise<{ authenticated: boolean; user?: AuthUser }> {
  const token = getToken();

  debugLog(1, '[Auth] Checking auth status...');
  if (!token) {
    debugLog(1, '[Auth] No token found');
    return { authenticated: false };
  }

  debugLog(1, '[Auth] Token found, validating with backend...');
  try {
    const response = await fetch(`${API_URL}/api/validate-token`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      debugLog(1, '[Auth] Token validation failed - invalid response');
      removeToken();
      return { authenticated: false };
    }

    const data = await response.json();
    debugLog(1, '[Auth] Validation response:', data);

    if (data.valid && data.authenticated) {
      debugLog(1, '[Auth] User authenticated:', data.user);
      return { authenticated: true, user: data.user };
    } else {
      debugLog(1, '[Auth] Token is not valid');
      removeToken();
      return { authenticated: false };
    }
  } catch (error) {
    console.error('[Auth] Auth status check failed:', error);
    removeToken();
    return { authenticated: false };
  }
}

/**
 * Redirect to login page
 */
export function redirectToLogin(email?: string): void {
  const loginUrl = email ? `${API_URL}/login?email=${encodeURIComponent(email)}` : `${API_URL}/login`;
  debugLog(1, '[Auth] Redirecting to login:', loginUrl);
  window.location.href = loginUrl;
}

/**
 * Logout user and clear token
 */
export async function logout(): Promise<void> {
  const token = getToken();

  try {
    if (token) {
      await fetch(`${API_URL}/api/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    }
  } catch (error) {
    console.error('Logout request failed:', error);
  } finally {
    removeToken();
  }
}

/**
 * Get authorization headers for API requests
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getToken();

  if (token) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  return {
    'Content-Type': 'application/json',
  };
}

/**
 * Check URL for token parameter (from OAuth callback redirect)
 * and store it if present
 */
export function handleTokenFromURL(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  if (token) {
    debugLog(1, '[Auth] Token found in URL');
    storeToken(token);
    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);
    debugLog(1, '[Auth] URL cleaned up');
    return true;
  }

  debugLog(1, '[Auth] No token in URL');
  return false;
}

/**
 * Fetch user information including roles from /api/me
 */
export async function fetchUserInfo(): Promise<AuthUser | null> {
  const token = getToken();

  if (!token) {
    debugLog(1, '[Auth] No token available for /api/me');
    return null;
  }

  try {
    debugLog(1, '[Auth] Fetching user info from /api/me...');
    const response = await fetch(`${API_URL}/api/me`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      console.error('[Auth] Failed to fetch user info:', response.status);
      return null;
    }

    const data = await response.json();
    debugLog(1, '[Auth] User info received:', data);

    return data;
  } catch (error) {
    console.error('[Auth] Error fetching user info:', error);
    return null;
  }
}
